import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { organizationApi } from '@/api/organization/organization.api';
import type { OrganizationBrandingResponse } from '@/api/organization/organization.types';
import { useAuth } from '@/contexts/AuthContext';
import type { BrandingConfig, ThemeMode } from '@/types/ConfigOrg';
import {
  applyBrandCssVariablesToRoot,
  applyFaviconToDocument,
  resetBrandCssVariablesOnRoot,
} from '@/utils/ConfigOrg/brandColorUtils';
import { mapBrandingResponseToState } from '@/utils/ConfigOrg/brandingMapper';
import { INITIAL_BRANDING_CONFIG } from '@/utils/ConfigOrg/constants';

interface BrandingContextState {
  branding: BrandingConfig;
  isConfigured: boolean;
  isLoading: boolean;
  refreshBranding: () => Promise<void>;
  applyBranding: (config: BrandingConfig) => void;
}

const BrandingContext = createContext<BrandingContextState | null>(null);

export function BrandingProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated, user } = useAuth();
  const [branding, setBranding] = useState<BrandingConfig>(INITIAL_BRANDING_CONFIG);
  const [isConfigured, setIsConfigured] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const applyBranding = useCallback((config: BrandingConfig) => {
    setBranding(config);
  }, []);

  const refreshBranding = useCallback(async () => {
    if (!isAuthenticated) return;

    setIsLoading(true);
    try {
      const { data } = await organizationApi.getUiConfig();
      setIsConfigured(data.isConfigured);
      applyBranding(mapBrandingResponseToState(data));
    } catch {
      setIsConfigured(false);
      applyBranding(INITIAL_BRANDING_CONFIG);
    } finally {
      setIsLoading(false);
    }
  }, [applyBranding, isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) {
      setBranding(INITIAL_BRANDING_CONFIG);
      setIsConfigured(false);
      resetBrandCssVariablesOnRoot();
      applyFaviconToDocument(null);
      return;
    }

    void refreshBranding();
  }, [isAuthenticated, user?.organizationId, refreshBranding]);

  const value = useMemo(
    () => ({
      branding,
      isConfigured,
      isLoading,
      refreshBranding,
      applyBranding,
    }),
    [branding, isConfigured, isLoading, refreshBranding, applyBranding],
  );

  return <BrandingContext.Provider value={value}>{children}</BrandingContext.Provider>;
}

export function useBranding(): BrandingContextState {
  const context = useContext(BrandingContext);
  if (!context) {
    throw new Error('useBranding must be used within a BrandingProvider');
  }
  return context;
}
